import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { usePermisoOperaciones } from './permisos';
import './office_panel.css';
import './consultas_ia.css';
import './agentes.css';
import './soporte.css';

// Pantalla "Soporte" (superadmin): el buzon soporte@nawioffice.online recibe pedidos por mail, una capa
// semantica (BM25 + embeddings locales) y Claude Haiku los interpretan, y cada orden se ejecuta cuando el
// remitente la confirma respondiendo SI (el reseteo de contraseña, ademas, cuando un superadmin la aprueba).
const GET_ESTADO = gql`
  query GetSoporteOffice {
    getSoporteOffice {
      activo casilla casillaConfigurada llmConfigurado procesoActivo latido ultimaVuelta
      esperandoConfirmacion esperandoAprobacion ejecutadas fallidas correos24h permisos { clave nombre }
    }
  }
`;
const CAMPOS_ORDEN = 'id accion accionNombre estado de nombreDe asunto texto resumen objetivo pregunta requiereAprobacion creado confirmadaEn aprobadaPor aprobadaEn rechazadaPor motivoRechazo ejecutadaEn resultado error venceEn inferencia';
const GET_ORDENES = gql`query GetOrdenesSoporte($estado: String, $pagina: Int) { getOrdenesSoporte(estado: $estado, pagina: $pagina, porPagina: 20) { total pagina paginas items { ${CAMPOS_ORDEN} } } }`;
const GET_CORREOS = gql`query GetCorreosSoporte($pagina: Int) { getCorreosSoporte(pagina: $pagina, porPagina: 25) { total pagina paginas items { id de nombreDe asunto recibido estado motivo ordenId autenticacion } } }`;
const GET_REMITENTES = gql`query GetRemitentesSoporte { getRemitentesSoporte { id email nombre permisos creado creadoPor actualizado actualizadoPor } }`;
const GUARDAR_REMITENTE = gql`mutation GR($email: String!, $nombre: String, $permisos: [String!]!) { guardarRemitenteSoporte(email: $email, nombre: $nombre, permisos: $permisos) { id } }`;
const QUITAR_REMITENTE = gql`mutation QR($id: ID!) { quitarRemitenteSoporte(id: $id) }`;
const APROBAR = gql`mutation AO($id: ID!) { aprobarOrdenSoporte(id: $id) { id estado } }`;
const RECHAZAR = gql`mutation RO($id: ID!, $motivo: String) { rechazarOrdenSoporte(id: $id, motivo: $motivo) { id estado } }`;
const ACTIVAR = gql`mutation AS($activo: Boolean!) { activarSoporte(activo: $activo) { activo } }`;
const PROBAR = gql`mutation PS($de: String!, $asunto: String, $texto: String!) { probarCorreoSoporte(de: $de, asunto: $asunto, texto: $texto) }`;

const TZ = 'America/Argentina/Buenos_Aires';
const PESTANAS = [['resumen', 'Resumen'], ['ordenes', 'Órdenes'], ['remitentes', 'Remitentes autorizados'], ['correos', 'Mails recibidos']];
const ESTADOS = {
  esperando_confirmacion: ['tag--yellow', 'Esperando que el remitente confirme'],
  esperando_aprobacion: ['tag--purple', 'Esperando aprobación de un superadmin'],
  aclaracion: ['tag--yellow', 'Esperando una aclaración'],
  confirmada: ['tag--blue', 'Confirmada'],
  aprobada: ['tag--blue', 'Aprobada'],
  ejecutando: ['tag--blue', 'Ejecutándose'],
  ejecutada: ['tag--green', 'Ejecutada'],
  fallida: ['tag--red', 'Falló'],
  cancelada: ['tag--gray', 'Cancelada por el remitente'],
  rechazada: ['tag--gray', 'Rechazada'],
  vencida: ['tag--gray', 'Vencida'],
  sin_orden: ['tag--gray', 'Sin orden'],
};
const ESTADOS_CORREO = { procesado: ['tag--green', 'Procesado'], ignorado: ['tag--gray', 'Ignorado'], error: ['tag--red', 'Error'], nuevo: ['tag--blue', 'Nuevo'] };
// Titulos cortos para las columnas de la tabla de remitentes.
const CORTO = { fecha_nacimiento: 'Fecha de nacimiento', cambiar_nombre: 'Nombre y apellido', bloquear_chat: 'Bloquear en el chat', quitar_de_cuenta: 'Quitar de una cuenta', alta_cuenta: 'Alta de cuenta' };
const FILTROS = [['pendientes', 'Pendientes'], ['', 'Todas'], ['ejecutada', 'Ejecutadas'], ['fallida', 'Fallidas']];

const fechaLarga = (iso) => (iso ? new Date(iso).toLocaleString('es-AR', { timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : '');
function haceCuanto(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'hace instantes';
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
  if (s < 86400) return `hace ${Math.floor(s / 3600)} h`;
  const d = Math.floor(s / 86400);
  return d === 1 ? 'hace 1 día' : `hace ${d} días`;
}
const errorDe = (e) => (e && (e.graphQLErrors?.[0]?.message || e.message)) || 'No se pudo completar la operación.';
const Aviso = ({ tipo = 'info', children }) => <div className={`ag-aviso ag-aviso--${tipo}`}>{children}</div>;
const Estado = ({ e, mapa = ESTADOS }) => { const [c, t] = mapa[e] || ['tag--gray', e]; return <span className={`tag ${c}`}>{t}</span>; };

// ── Inferencia: lo que vio la capa semantica y lo que decidio el LLM ────────────────
function Inferencia({ inf, decision }) {
  if (!inf) return null;
  const contextos = inf.contextos || [];
  const llm = decision || inf.llm || {};
  return (
    <div className="sp-inferencia">
      <div className="sp-bloque">
        <h4>Capa semántica: los 3 contextos</h4>
        <ol className="sp-contextos">
          {contextos.map((c) => (
            <li key={c.numero || c.clave}>
              <b>{c.nombre}</b>
              <span className="ag-gris"> · puntaje {c.puntaje} · BM25 {c.bm25} · semántica {c.semantica}</span>
            </li>
          ))}
        </ol>
        {inf.entidades && (inf.entidades.emails?.length > 0 || inf.entidades.fechas?.length > 0) && (
          <p className="ag-ayuda">Datos encontrados en el mail: {[...(inf.entidades.emails || []), ...(inf.entidades.fechas || [])].join(', ')}</p>
        )}
      </div>
      <div className="sp-bloque">
        <h4>LLM ({llm.modelo || 'Claude Haiku'})</h4>
        {(llm.pasos || []).length === 0 ? <p className="ag-ayuda">Sin pasos registrados.</p> : (
          <ul className="sp-pasos">
            {llm.pasos.map((p, i) => (
              <li key={i}><code>{p.herramienta}</code> <span className="ag-gris">{JSON.stringify(p.entrada)}</span>{p.salida && <div className="sp-salida">{typeof p.salida === 'string' ? p.salida : JSON.stringify(p.salida)}</div>}</li>
            ))}
          </ul>
        )}
        {llm.error && <p className="ag-error">{llm.error}</p>}
        {llm.motivo && <p className="ag-ayuda">Motivo: {llm.motivo}</p>}
        {llm.uso && <p className="ag-ayuda">Tokens: {llm.uso.entrada} de entrada, {llm.uso.salida} de salida.</p>}
      </div>
    </div>
  );
}

// ── Resumen y prueba ────────────────────────────────────────────────────────────────
function Probar({ onFlash }) {
  const [f, setF] = useState({ de: '', asunto: '', texto: '' });
  const [probar, { loading }] = useMutation(PROBAR);
  const [r, setR] = useState(null);
  const enviar = async (e) => {
    e.preventDefault();
    setR(null);
    try {
      const { data } = await probar({ variables: { de: f.de.trim(), asunto: f.asunto, texto: f.texto } });
      setR(data.probarCorreoSoporte);
    } catch (err) { onFlash(errorDe(err), 'error'); }
  };
  const d = r && r.decision;
  return (
    <section className="panel-card ag-seccion">
      <h3 className="ag-titulo">Probar un mail</h3>
      <p className="ag-ayuda ag-ayuda--arriba">Muestra cómo se interpretaría un mail, como si lo mandara ese remitente. No crea órdenes ni manda nada.</p>
      <form onSubmit={enviar}>
        <div className="ag-form-fila">
          <label className="ag-campo"><span>De</span><input type="email" required value={f.de} placeholder="remitente@empresa.com" onChange={(e) => setF({ ...f, de: e.target.value })} /></label>
          <label className="ag-campo"><span>Asunto</span><input type="text" value={f.asunto} maxLength={300} onChange={(e) => setF({ ...f, asunto: e.target.value })} /></label>
        </div>
        <label className="ag-campo"><span>Texto</span><textarea rows={5} required value={f.texto} maxLength={20000} onChange={(e) => setF({ ...f, texto: e.target.value })} /></label>
        <button className="btn-accent" type="submit" disabled={loading || !f.de.trim() || !f.texto.trim()}>{loading ? 'Interpretando…' : 'Interpretar'}</button>
      </form>
      {r && (
        <div className="sp-resultado">
          <p className="ag-ayuda">
            Remitente: <b>{r.remitente.nombre}</b> · {r.remitente.permisos.length ? `puede pedir ${r.remitente.permisos.length} órdenes` : 'sin órdenes autorizadas'}{r.remitente.usuarioChat ? ' · es usuario de NAWI Chat' : ''}
          </p>
          <div className={`sp-decision sp-decision--${d.tipo}`}>
            {d.tipo === 'orden' && <><b>Orden propuesta:</b> {d.resumen}<br /><span className="ag-gris">Se le pediría al remitente que confirme respondiendo SI.</span></>}
            {d.tipo === 'aclaracion' && <><b>Pediría una aclaración:</b> {d.pregunta}</>}
            {d.tipo === 'sin_orden' && <><b>Sin orden.</b> Respondería: {d.respuesta}</>}
            {d.tipo === 'error' && <><b>Error:</b> {d.error}</>}
          </div>
          <Inferencia inf={{ contextos: r.contextos, entidades: r.entidades }} decision={d} />
        </div>
      )}
    </section>
  );
}

function Resumen({ e, operaciones, ir, onFlash, refetch }) {
  const [activar, { loading }] = useMutation(ACTIVAR);
  const cambiar = async (v) => {
    try { await activar({ variables: { activo: v } }); onFlash(v ? 'Lectura del buzón activada.' : 'Lectura del buzón pausada.'); refetch(); } catch (err) { onFlash(errorDe(err), 'error'); }
  };
  return (
    <>
      {!e.procesoActivo && <Aviso tipo="error">El proceso de soporte no está funcionando{e.latido ? ` desde ${fechaLarga(e.latido)}` : ''}: no se leen mails ni se ejecutan órdenes.</Aviso>}
      {e.procesoActivo && !e.casillaConfigurada && <Aviso tipo="atencion">La casilla {e.casilla} todavía no está configurada en el servidor.</Aviso>}
      {e.procesoActivo && !e.llmConfigurado && <Aviso tipo="atencion">Falta la clave de Claude en el servidor: los mails no se pueden interpretar.</Aviso>}
      {e.esperandoAprobacion > 0 && (
        <Aviso tipo="atencion">Hay <b>{e.esperandoAprobacion}</b> {e.esperandoAprobacion === 1 ? 'orden esperando' : 'órdenes esperando'} tu aprobación. <button className="btn-subtle ag-enlace" onClick={() => ir('ordenes')}>Ver órdenes</button></Aviso>
      )}
      <section className="panel-card ag-seccion ag-cabecera-agente">
        <div>
          <h2 className="ag-nombre">{e.casilla}</h2>
          <p className="ag-tarjeta-texto">
            Lee los mails nuevos cada minuto. Responde solo a remitentes autorizados y a usuarios de NAWI Chat (para su propio reseteo de contraseña); el resto se ignora.
            {e.procesoActivo ? ` Última vuelta ${haceCuanto(e.latido)}.` : ''}
          </p>
        </div>
        <label className="ag-interruptor">
          <input type="checkbox" checked={e.activo} disabled={!operaciones || loading} onChange={(x) => cambiar(x.target.checked)} />
          <span className="ag-interruptor-pista" aria-hidden="true"><span /></span>
          <span className="ag-interruptor-texto">{e.activo ? 'Leyendo el buzón' : 'Pausado'}</span>
        </label>
      </section>
      <div className="sp-kpis">
        {[['Mails en 24 h', e.correos24h], ['Esperando confirmación', e.esperandoConfirmacion], ['Esperando aprobación', e.esperandoAprobacion], ['Ejecutadas', e.ejecutadas], ['Fallidas', e.fallidas]].map(([t, n]) => (
          <div key={t} className="panel-card sp-kpi"><span>{t}</span><b>{n}</b></div>
        ))}
      </div>
      <section className="panel-card ag-seccion">
        <h3 className="ag-titulo">Cómo funciona</h3>
        <ol className="sp-pasos-lista">
          <li>Llega un mail. Si el remitente no está autorizado ni es usuario de NAWI Chat, se ignora.</li>
          <li>BM25 y un modelo de embeddings que corre en el servidor arman los 3 contextos más probables, con los datos de la base.</li>
          <li>Claude Haiku lee el mail y los contextos, busca lo que falte y propone una orden, pide un dato o responde que no hay orden.</li>
          <li>El remitente recibe un mail con el resumen y responde SI para confirmar. Sin esa respuesta no se hace nada; vence a las 48 horas.</li>
          <li>Se ejecuta con la API de Office y queda en el registro de actividad. El reseteo de contraseña espera además la aprobación de un superadmin.</li>
        </ol>
      </section>
      {operaciones && <Probar onFlash={onFlash} />}
    </>
  );
}

// ── Ordenes ───────────────────────────────────────────────────────────────────────
function TarjetaOrden({ o, onFlash, refetch }) {
  const [abierta, setAbierta] = useState(false);
  const [rechazando, setRechazando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [aprobar, { loading: aprobandoM }] = useMutation(APROBAR);
  const [rechazar, { loading: rechazandoM }] = useMutation(RECHAZAR);
  const pendiente = ['esperando_confirmacion', 'esperando_aprobacion', 'aclaracion'].includes(o.estado);
  const accion = async (fn, vars, ok) => {
    try { await fn({ variables: vars }); onFlash(ok); setRechazando(false); refetch(); } catch (err) { onFlash(errorDe(err), 'error'); }
  };
  return (
    <article className="panel-card ag-envio">
      <header className="ag-envio-cabeza">
        <Estado e={o.estado} />
        <b>{o.accionNombre || (o.estado === 'aclaracion' ? 'Pedido incompleto' : 'Pedido')}</b>
        <span className="ag-gris">{o.nombreDe ? `${o.nombreDe} · ` : ''}{o.de} · {fechaLarga(o.creado)}</span>
      </header>
      {o.resumen && <p className="sp-resumen">{o.resumen}</p>}
      {o.pregunta && <p className="sp-resumen">Se le preguntó: {o.pregunta}</p>}
      {o.resultado && <p className="ag-ayuda">{o.resultado}</p>}
      {o.error && <p className="ag-error">Error: {o.error}</p>}
      {o.estado === 'rechazada' && <p className="ag-ayuda">Rechazada por {o.rechazadaPor}{o.motivoRechazo ? `: ${o.motivoRechazo}` : ''}.</p>}
      {o.aprobadaPor && <p className="ag-ayuda">Aprobada por {o.aprobadaPor} el {fechaLarga(o.aprobadaEn)}.</p>}
      {pendiente && o.venceEn && <p className="ag-ayuda">Vence {fechaLarga(o.venceEn)}.</p>}
      {abierta && (
        <div className="sp-detalle">
          <div className="ag-mail">
            <div className="ag-mail-cabeza"><span>Mail original</span></div>
            <div className="ag-mail-asunto">{o.asunto || '(sin asunto)'}</div>
            <div className="ag-mail-cuerpo">{o.texto}</div>
          </div>
          <Inferencia inf={o.inferencia} />
        </div>
      )}
      {rechazando && (
        <div className="ag-form-fila">
          <label className="ag-campo"><span>Motivo (se le cuenta al remitente)</span><input type="text" value={motivo} maxLength={300} onChange={(e) => setMotivo(e.target.value)} /></label>
          <button className="btn-danger" disabled={rechazandoM} onClick={() => accion(rechazar, { id: o.id, motivo: motivo.trim() || null }, 'Orden rechazada.')}>Rechazar</button>
          <button className="btn-subtle" onClick={() => setRechazando(false)}>Cancelar</button>
        </div>
      )}
      <div className="ag-envio-acciones">
        <button className="btn-subtle" onClick={() => setAbierta(!abierta)}>{abierta ? 'Ocultar detalle' : 'Ver mail e interpretación'}</button>
        <span className="ag-espacio" />
        {pendiente && !rechazando && <button className="btn-subtle" onClick={() => setRechazando(true)}>Rechazar</button>}
        {o.estado === 'esperando_aprobacion' && (
          <button className="btn-accent" disabled={aprobandoM} onClick={() => accion(aprobar, { id: o.id }, 'Orden aprobada: se ejecuta en unos segundos.')}>{aprobandoM ? 'Aprobando…' : 'Aprobar y ejecutar'}</button>
        )}
      </div>
    </article>
  );
}

function Ordenes({ onFlash }) {
  const [filtro, setFiltro] = useState('pendientes');
  const [pagina, setPagina] = useState(1);
  const { data, loading, refetch } = useQuery(GET_ORDENES, { variables: { estado: filtro || null, pagina }, fetchPolicy: 'network-only', pollInterval: 30000 });
  const r = data?.getOrdenesSoporte;
  return (
    <>
      <div className="sp-filtros" role="group" aria-label="Filtrar órdenes">
        {FILTROS.map(([k, t]) => (
          <button key={k} type="button" aria-pressed={filtro === k} className={`ag-dia ${filtro === k ? 'ag-dia--elegido' : ''}`} onClick={() => { setFiltro(k); setPagina(1); }}>{t}</button>
        ))}
      </div>
      {loading && !r ? <div className="spinner" /> : !r || r.items.length === 0 ? (
        <div className="panel-card empty-state">{filtro === 'pendientes' ? 'No hay órdenes pendientes.' : 'Todavía no hay órdenes.'}</div>
      ) : (
        <>
          {r.items.map((o) => <TarjetaOrden key={o.id} o={o} onFlash={onFlash} refetch={refetch} />)}
          {r.paginas > 1 && (
            <div className="ag-paginas">
              <button className="btn-outline" disabled={r.pagina <= 1} onClick={() => setPagina(r.pagina - 1)}>‹ Anterior</button>
              <span>Página {r.pagina} de {r.paginas}</span>
              <button className="btn-outline" disabled={r.pagina >= r.paginas} onClick={() => setPagina(r.pagina + 1)}>Siguiente ›</button>
            </div>
          )}
        </>
      )}
    </>
  );
}

// ── Remitentes autorizados ──────────────────────────────────────────────────────────
function FilaRemitente({ r, permisos, editable, onFlash, refetch }) {
  const [ps, setPs] = useState(r.permisos);
  const [aQuitar, setAQuitar] = useState(false);
  const [guardar, { loading }] = useMutation(GUARDAR_REMITENTE);
  const [quitar] = useMutation(QUITAR_REMITENTE);
  useEffect(() => setPs(r.permisos), [r.permisos]);
  const cambiado = [...ps].sort().join() !== [...r.permisos].sort().join();
  const toggle = (k) => setPs(ps.includes(k) ? ps.filter((x) => x !== k) : [...ps, k]);
  const salvar = async () => {
    try { await guardar({ variables: { email: r.email, nombre: r.nombre, permisos: ps } }); onFlash(`Permisos de ${r.email} guardados.`); refetch(); } catch (err) { onFlash(errorDe(err), 'error'); }
  };
  const borrar = async () => {
    try { await quitar({ variables: { id: r.id } }); onFlash(`${r.email} ya no puede dar órdenes por mail.`); refetch(); } catch (err) { onFlash(errorDe(err), 'error'); }
  };
  return (
    <tr>
      <td><b>{r.nombre || r.email.split('@')[0]}</b><div className="ag-gris">{r.email}</div></td>
      {permisos.map((p) => (
        <td key={p.clave} className="sp-centro">
          <input type="checkbox" aria-label={`${p.nombre} para ${r.email}`} checked={ps.includes(p.clave)} disabled={!editable} onChange={() => toggle(p.clave)} />
        </td>
      ))}
      <td className="ag-gris">{fechaLarga(r.actualizado || r.creado)}{(r.actualizadoPor || r.creadoPor) ? ` · ${r.actualizadoPor || r.creadoPor}` : ''}</td>
      <td className="ag-derecha">
        {editable && (aQuitar ? (
          <span className="ag-confirmar">
            <button className="btn-danger" onClick={borrar}>Quitar</button>
            <button className="btn-subtle" onClick={() => setAQuitar(false)}>Cancelar</button>
          </span>
        ) : (
          <span className="ag-confirmar">
            {cambiado && <button className="btn-accent" disabled={loading} onClick={salvar}>Guardar</button>}
            {cambiado && <button className="btn-subtle" onClick={() => setPs(r.permisos)}>Deshacer</button>}
            {!cambiado && <button className="btn-subtle" onClick={() => setAQuitar(true)}>Quitar</button>}
          </span>
        ))}
      </td>
    </tr>
  );
}

function Remitentes({ permisos, operaciones, onFlash }) {
  const { data, loading, refetch } = useQuery(GET_REMITENTES, { fetchPolicy: 'network-only' });
  const [guardar, { loading: agregando }] = useMutation(GUARDAR_REMITENTE);
  const [nuevo, setNuevo] = useState({ email: '', nombre: '', permisos: [] });
  const xs = data?.getRemitentesSoporte || [];
  const agregar = async (e) => {
    e.preventDefault();
    try {
      await guardar({ variables: { email: nuevo.email.trim(), nombre: nuevo.nombre.trim() || null, permisos: nuevo.permisos } });
      onFlash(`${nuevo.email.trim()} agregado.`);
      setNuevo({ email: '', nombre: '', permisos: [] });
      refetch();
    } catch (err) { onFlash(errorDe(err), 'error'); }
  };
  return (
    <>
      <section className="panel-card ag-seccion">
        <h2 className="ag-nombre">Remitentes autorizados</h2>
        <p className="ag-tarjeta-texto">
          Quién puede dar cada orden por mail. Igual tiene que confirmarla respondiendo SI, desde la misma casilla. El reseteo de contraseña no va acá: lo pide cada usuario de NAWI Chat para su propia cuenta, y lo aprueba un superadmin.
        </p>
        {!operaciones && <Aviso tipo="info">Solo los superadmins con permiso Operaciones pueden cambiar esta lista.</Aviso>}
        {operaciones && (
          <form onSubmit={agregar}>
            <div className="ag-form-fila">
              <label className="ag-campo"><span>Email</span><input type="email" required value={nuevo.email} placeholder="nombre@empresa.com" onChange={(e) => setNuevo({ ...nuevo, email: e.target.value })} /></label>
              <label className="ag-campo"><span>Nombre</span><input type="text" value={nuevo.nombre} maxLength={120} placeholder="Opcional" onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} /></label>
            </div>
            <div className="sp-permisos-nuevo">
              {permisos.map((p) => (
                <label key={p.clave} className="ag-campo ag-campo--check">
                  <input type="checkbox" checked={nuevo.permisos.includes(p.clave)} onChange={(e) => setNuevo({ ...nuevo, permisos: e.target.checked ? [...nuevo.permisos, p.clave] : nuevo.permisos.filter((x) => x !== p.clave) })} />
                  <span>{p.nombre}</span>
                </label>
              ))}
            </div>
            <button className="btn-accent" type="submit" disabled={agregando || !nuevo.email.trim()}>{agregando ? 'Agregando…' : 'Agregar remitente'}</button>
          </form>
        )}
      </section>
      <section className="panel-card ag-seccion">
        <h3 className="ag-titulo">{xs.length === 1 ? '1 remitente' : `${xs.length} remitentes`}</h3>
        {loading && !data ? <div className="spinner" /> : xs.length === 0 ? <div className="empty-state">Todavía nadie puede dar órdenes por mail.</div> : (
          <div className="cia-tabla-scroll">
            <table className="cia-tabla ag-tabla sp-tabla">
              <thead><tr><th>Remitente</th>{permisos.map((p) => <th key={p.clave} className="sp-centro" title={p.nombre}>{CORTO[p.clave] || p.nombre}</th>)}<th>Último cambio</th><th /></tr></thead>
              <tbody>{xs.map((r) => <FilaRemitente key={r.id} r={r} permisos={permisos} editable={operaciones} onFlash={onFlash} refetch={refetch} />)}</tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

// ── Mails recibidos ─────────────────────────────────────────────────────────────────
function Correos() {
  const [pagina, setPagina] = useState(1);
  const { data, loading } = useQuery(GET_CORREOS, { variables: { pagina }, fetchPolicy: 'network-only', pollInterval: 30000 });
  const r = data?.getCorreosSoporte;
  const verif = (a) => (a ? ['spf', 'dkim', 'dmarc'].map((k) => `${k.toUpperCase()} ${a[k] || '—'}`).join(' · ') : '—');
  return (
    <section className="panel-card ag-seccion">
      <h3 className="ag-titulo">Mails recibidos {r ? `(${r.total})` : ''}</h3>
      {loading && !r ? <div className="spinner" /> : !r || r.items.length === 0 ? <div className="empty-state">Todavía no llegó ningún mail.</div> : (
        <>
          <div className="cia-tabla-scroll">
            <table className="cia-tabla ag-tabla sp-tabla-correos">
              <thead><tr><th>Recibido</th><th>De</th><th>Asunto</th><th>Estado</th><th>Qué se hizo</th><th>Verificación</th></tr></thead>
              <tbody>
                {r.items.map((c) => (
                  <tr key={c.id}>
                    <td className="ag-gris">{fechaLarga(c.recibido)}</td>
                    <td>{c.nombreDe ? <><b>{c.nombreDe}</b><div className="ag-gris">{c.de}</div></> : c.de || '—'}</td>
                    <td className="sp-asunto">{c.asunto || '(sin asunto)'}</td>
                    <td><Estado e={c.estado} mapa={ESTADOS_CORREO} /></td>
                    <td className="sp-motivo">{c.motivo || '—'}</td>
                    <td className="ag-gris sp-verif">{verif(c.autenticacion)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {r.paginas > 1 && (
            <div className="ag-paginas">
              <button className="btn-outline" disabled={r.pagina <= 1} onClick={() => setPagina(r.pagina - 1)}>‹ Anterior</button>
              <span>Página {r.pagina} de {r.paginas}</span>
              <button className="btn-outline" disabled={r.pagina >= r.paginas} onClick={() => setPagina(r.pagina + 1)}>Siguiente ›</button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ── Pagina ─────────────────────────────────────────────────────────────────────────
export default function Soporte() {
  const { isSuperadmin, logout } = useAuth();
  const { operaciones } = usePermisoOperaciones();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const pestana = PESTANAS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'resumen';
  const [flash, setFlash] = useState(null);
  const { data, loading, error, refetch } = useQuery(GET_ESTADO, { fetchPolicy: 'network-only', skip: !isSuperadmin, pollInterval: isSuperadmin ? 30000 : 0 });
  const e = data?.getSoporteOffice;

  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), flash.tipo === 'error' ? 8000 : 4000);
    return () => clearTimeout(t);
  }, [flash]);

  if (!isSuperadmin) {
    return (
      <div className="page-container" style={{ maxWidth: 900 }}>
        <div className="panel-card" style={{ padding: 40, textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 8 }}>Acceso denegado</h2>
          <p style={{ color: '#6b7280', marginBottom: 16 }}>Solo los superadmins pueden ver el buzón de soporte.</p>
          <button className="btn-accent" onClick={() => navigate('/altaEmpresa')}>Volver</button>
        </div>
      </div>
    );
  }

  const ir = (k) => { setParams(k === 'resumen' ? {} : { tab: k }); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const onFlash = (texto, tipo = 'ok') => setFlash({ texto, tipo });

  return (
    <div className="page-container cia ag" style={{ maxWidth: 1100 }}>
      <div className="office-header">
        <div className="office-header-left">
          <button className="btn-subtle" onClick={() => navigate('/altaEmpresa')}>← Volver</button>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 700, letterSpacing: '-0.3px', margin: 0 }}>Soporte</h1>
          <span className="ag-subtitulo">Órdenes que llegan por mail</span>
        </div>
        <button className="btn-subtle" onClick={() => logout()}>Cerrar sesión</button>
      </div>

      <div className="cia-tabs ag-pestanas" role="tablist">
        {PESTANAS.map(([k, t]) => (
          <button key={k} role="tab" aria-selected={pestana === k} className={`cia-tab ${pestana === k ? 'cia-tab--activa' : ''}`} onClick={() => ir(k)}>
            {t}{k === 'ordenes' && e && e.esperandoAprobacion > 0 && <span className="ag-contador">{e.esperandoAprobacion}</span>}
          </button>
        ))}
      </div>

      {flash && <div className={`ag-flash ag-flash--${flash.tipo}`} role="status">{flash.texto}</div>}
      {error && <Aviso tipo="error">{errorDe(error)}</Aviso>}
      {loading && !e && <div className="spinner" />}

      {e && pestana === 'resumen' && <Resumen e={e} operaciones={operaciones} ir={ir} onFlash={onFlash} refetch={refetch} />}
      {pestana === 'ordenes' && <Ordenes onFlash={onFlash} />}
      {e && pestana === 'remitentes' && <Remitentes permisos={e.permisos} operaciones={operaciones} onFlash={onFlash} />}
      {pestana === 'correos' && <Correos />}
    </div>
  );
}
